import { EVENT5K } from "@/constants/testIds";
import { Footprints, PersonStanding, Users2, Shirt, Heart, ExternalLink } from "lucide-react";
import { goToRunSignup } from "@/lib/runsignup";

export default function EventOptions({ onRegister }) {
  const options = [
    {
      icon: Footprints,
      title: "5K Run/Walk",
      accent: "#00A651",
      distance: "3.1 Miles",
      tagline: "For runners, walkers, teams, and anyone chasing a personal best.",
      bullets: [
        "All-pace-friendly course",
        "Water station + support",
        "Open to all ability levels",
        "Register solo or as a team",
      ],
      target: "register5K",
    },
    {
      icon: PersonStanding,
      title: "1-Mile Fun Run",
      accent: "#EC008C",
      distance: "1 Mile",
      tagline: "Perfect for youth, families, beginners, and first-time finishers.",
      bullets: [
        "Kid + stroller friendly",
        "Family walking option",
        "No pace, all vibes",
        "Finisher energy for everyone",
      ],
      target: "register1Mile",
    },
    {
      icon: Users2,
      title: "Community Teams",
      accent: "#00A651",
      distance: "Group",
      tagline: "Rally coworkers, friends, gym members, and family through RunSignup.",
      bullets: [
        "Create or join a team",
        "Team leaderboard on RunSignup",
        "Perfect for gyms & churches",
        "Rep your name at the finish",
      ],
      target: "team",
    },
    {
      icon: Shirt,
      title: "Optional Event Shirt",
      accent: "#EC008C",
      distance: "Add-On",
      tagline: "Add an official event shirt at RunSignup checkout — youth to Adult XL: $12 · 2XL/3XL: $14.",
      bullets: [
        "Youth – Adult XL: +$12",
        "Adult 2XL & 3XL: +$14",
        "Guaranteed by Oct 16, 2026",
        "After Oct 16: not guaranteed",
      ],
      target: "register",
    },
  ];

  return (
    <section
      id="options"
      data-testid={EVENT5K.sectionOptions}
      className="relative bg-[#0A0A0A] py-24 md:py-32 border-t border-[#27272A]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#EC008C] font-bold mb-4">Pick your distance</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white">
            Two ways to <span className="text-[#00A651]">show up</span>.
          </h2>
          <p className="mt-4 text-lg text-[#A3A3A3]">
            Run, walk, bring your family, form a team, or just come out to support entrepreneurs.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6 md:gap-8">
          {options.map(({ icon: Icon, title, accent, distance, tagline, bullets, target }, i) => (
            <div
              key={title}
              className="group relative bg-[#141414] border border-[#27272A] p-8 md:p-10 hover:border-[#00A651]/60 transition-all"
            >
              <div className="absolute top-0 left-0 h-1 w-24" style={{ background: accent }} />
              <div className="flex items-start justify-between">
                <Icon className="w-10 h-10" style={{ color: accent }} />
                <span className="font-display text-4xl font-black" style={{ color: accent }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <div className="mt-6 text-[11px] uppercase tracking-[0.3em]" style={{ color: accent }}>
                {distance}
              </div>
              <h3 className="mt-2 font-display text-4xl md:text-5xl font-black uppercase text-white tracking-tight">
                {title}
              </h3>
              <p className="mt-3 text-[#A3A3A3] text-base md:text-lg">{tagline}</p>
              <ul className="mt-6 space-y-2">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[#E5E5E5]">
                    <span className="mt-2 w-1.5 h-1.5" style={{ background: accent }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => goToRunSignup(target)}
                className="mt-8 inline-flex items-center gap-2 font-bold uppercase tracking-widest text-sm border-2 px-5 py-3 transition-all hover:-translate-y-0.5"
                style={{ borderColor: accent, color: accent }}
              >
                {title === "Community Teams" ? "Create or Join a Team" : title === "Optional Event Shirt" ? "Add a Shirt at Checkout" : `Register on RunSignup`}
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        {/* Donations note */}
        <div className="mt-6 bg-[#141414] border border-dashed border-[#EC008C]/50 p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Heart className="w-6 h-6 text-[#EC008C]" />
            <div>
              <div className="font-display text-lg font-bold uppercase text-white">Donations through RunSignup</div>
              <div className="text-sm text-[#A3A3A3]">
                Can&apos;t attend? Support entrepreneurs anyway. Add a donation at checkout.
              </div>
            </div>
          </div>
          <button
            onClick={() => goToRunSignup("donate")}
            className="bg-[#EC008C] hover:bg-[#FF1FA8] text-white font-bold uppercase tracking-widest text-xs px-5 py-3 transition-all inline-flex items-center gap-2"
          >
            Donate on RunSignup <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </section>
  );
}
